import React, { useState, useEffect, useRef } from 'react';
import enhancedAPI, { 
  FileMetadata, FileStats, FileProcessingStatus, 
  FileProcessingResult, ResetFileStatusResult 
} from '../services/enhancedApi';
import { 
  FiUpload, FiFile, FiDownload, FiTrash2, FiRefreshCw, 
  FiTag, FiFolder, FiGlobe, FiClock, FiCheckCircle,
  FiAlertCircle, FiX, FiPlay, FiRotateCcw, FiSettings
} from './Icons';
import './FileManagement.css';

interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'uploading' | 'processing' | 'completed' | 'failed';
  error?: string;
}

const FileManagement: React.FC = () => {
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [fileStats, setFileStats] = useState<FileStats | null>(null);
  const [selectedScope, setSelectedScope] = useState<'global' | 'project'>('global');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Map<string, UploadProgress>>(new Map());
  const [processingStatus, setProcessingStatus] = useState<FileProcessingStatus | null>(null);
  const [showAdminControls, setShowAdminControls] = useState(false);
  const [adminProcessing, setAdminProcessing] = useState(false);

  const categories = [
    'financial_analysis',
    'market_intelligence',
    'customer_insights',
    'it_landscape',
    'risk_assessment',
    'compliance',
    'general'
  ];

  useEffect(() => {
    loadFiles();
    loadFileStats();
    loadProcessingStatus();
  }, [selectedScope, selectedCategory]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const fileList = await enhancedAPI.listFiles(
        selectedScope,
        undefined,
        selectedCategory || undefined
      );
      setFiles(fileList);
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFileStats = async () => {
    try {
      const stats = await enhancedAPI.getFileStats();
      setFileStats(stats);
    } catch (error) {
      console.error('Error loading file stats:', error);
    }
  };

  const loadProcessingStatus = async () => {
    try {
      const status = await enhancedAPI.getFileProcessingStatus();
      setProcessingStatus(status);
    } catch (error) {
      console.error('Error loading processing status:', error);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = async (fileList: FileList) => {
    setUploading(true);
    
    // Initialize progress for all files
    const newProgress = new Map(uploadProgress);
    Array.from(fileList).forEach(file => {
      newProgress.set(file.name, {
        fileName: file.name,
        progress: 0,
        status: 'uploading'
      });
    });
    setUploadProgress(newProgress);
    
    const uploadPromises = Array.from(fileList).map(file => 
      uploadFile(file)
    );
    
    try {
      await Promise.all(uploadPromises);
      await loadFiles();
      await loadFileStats();
      
      // Clear progress after 3 seconds
      setTimeout(() => {
        setUploadProgress(new Map());
      }, 3000);
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      setUploading(false);
    }
  };

  const uploadFile = async (file: File) => {
    try {
      const tags = file.name.split('.')[0].split('_');
      
      // Update progress to 50% when upload starts
      setUploadProgress(prev => {
        const newProgress = new Map(prev);
        const current = newProgress.get(file.name);
        if (current) {
          current.progress = 50;
        }
        return newProgress;
      });
      
      const result = await enhancedAPI.uploadFileWithProgress(
        file,
        selectedScope,
        undefined,
        selectedCategory || undefined,
        (progress) => {
          setUploadProgress(prev => {
            const newProgress = new Map(prev);
            const current = newProgress.get(file.name);
            if (current && current.status === 'uploading') {
              current.progress = Math.min(progress * 0.5, 50); // Scale to 0-50% for upload
            }
            return newProgress;
          });
        }
      );
      
      // Update to processing status
      setUploadProgress(prev => {
        const newProgress = new Map(prev);
        const current = newProgress.get(file.name);
        if (current) {
          current.progress = 75;
          current.status = 'processing';
        }
        return newProgress;
      });
      
      // If file was uploaded, start processing
      if (result && result.file_id) {
        try {
          await enhancedAPI.processFile(result.file_id, false);
          
          // Mark as completed
          setUploadProgress(prev => {
            const newProgress = new Map(prev);
            const current = newProgress.get(file.name);
            if (current) {
              current.progress = 100;
              current.status = 'completed';
            }
            return newProgress;
          });
        } catch (procError) {
          console.warn(`Processing will continue in background for ${file.name}`);
        }
      }
      
    } catch (error) {
      console.error(`Error uploading ${file.name}:`, error);
      
      // Mark as failed
      setUploadProgress(prev => {
        const newProgress = new Map(prev);
        const current = newProgress.get(file.name);
        if (current) {
          current.status = 'failed';
          current.error = error instanceof Error ? error.message : 'Upload failed';
        }
        return newProgress;
      });
      
      throw error;
    }
  };

  const deleteFile = async (fileId: string) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        // Add loading state for the specific file being deleted
        setFiles(prevFiles => 
          prevFiles.map(file => 
            file.file_id === fileId 
              ? { ...file, isDeleting: true } 
              : file
          )
        );
        
        await enhancedAPI.deleteFile(fileId);
        
        // Show success notification
        alert('File deleted successfully');
        
        // Reload files and stats
        await loadFiles();
        await loadFileStats();
      } catch (error) {
        console.error('Error deleting file:', error);
        
        // Remove loading state on error
        setFiles(prevFiles => 
          prevFiles.map(file => 
            file.file_id === fileId 
              ? { ...file, isDeleting: false } 
              : file
          )
        );
        
        // Show error notification
        alert(`Failed to delete file: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  const downloadFile = async (fileId: string, filename: string) => {
    try {
      const blob = await enhancedAPI.downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const processFile = async (fileId: string) => {
    try {
      await enhancedAPI.processFile(fileId, true);
      await loadFiles();
    } catch (error) {
      console.error('Error processing file:', error);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    // Display in UAE timezone (UTC+4)
    return new Date(dateString).toLocaleString('en-US', {
      timeZone: 'Asia/Dubai',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Admin functions
  const triggerManualProcessing = async () => {
    setAdminProcessing(true);
    try {
      const result = await enhancedAPI.triggerFileProcessing(5);
      alert(`Processing triggered for ${result.files_processed} files`);
      await loadFiles();
      await loadProcessingStatus();
    } catch (error) {
      console.error('Error triggering processing:', error);
      alert('Failed to trigger processing');
    } finally {
      setAdminProcessing(false);
    }
  };

  const resetFailedFiles = async () => {
    if (!window.confirm('Reset all failed files to PROCESSING status?')) return;
    
    setAdminProcessing(true);
    try {
      const result = await enhancedAPI.resetFileStatus({
        statusFilter: 'FAILED',
        targetStatus: 'PROCESSING'
      });
      alert(`Reset ${result.reset_count} files`);
      await loadFiles();
      await loadProcessingStatus();
    } catch (error) {
      console.error('Error resetting files:', error);
      alert('Failed to reset files');
    } finally {
      setAdminProcessing(false);
    }
  };

  const resetSelectedFiles = async () => {
    if (selectedFiles.size === 0) {
      alert('No files selected');
      return;
    }
    
    if (!window.confirm(`Reset ${selectedFiles.size} selected files to PROCESSING status?`)) return;
    
    setAdminProcessing(true);
    try {
      const result = await enhancedAPI.resetFileStatus({
        fileIds: Array.from(selectedFiles),
        targetStatus: 'PROCESSING'
      });
      alert(`Reset ${result.reset_count} files`);
      setSelectedFiles(new Set());
      await loadFiles();
      await loadProcessingStatus();
    } catch (error) {
      console.error('Error resetting selected files:', error);
      alert('Failed to reset files');
    } finally {
      setAdminProcessing(false);
    }
  };

  const toggleFileSelection = (fileId: string) => {
    const newSelection = new Set(selectedFiles);
    if (newSelection.has(fileId)) {
      newSelection.delete(fileId);
    } else {
      newSelection.add(fileId);
    }
    setSelectedFiles(newSelection);
  };

  return (
    <div className="file-management">
      <div className="file-header">
        <h2>File Management</h2>
        <div className="header-actions">
          <select
            value={selectedScope}
            onChange={(e) => setSelectedScope(e.target.value as 'global' | 'project')}
            className="scope-selector"
          >
            <option value="global">Global Files</option>
            <option value="project">Project Files</option>
          </select>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-selector"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat.replace('_', ' ').charAt(0).toUpperCase() + cat.slice(1).replace('_', ' ')}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowAdminControls(!showAdminControls)}
            className="btn btn-secondary"
            title="Admin Controls"
          >
            <FiSettings />
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn btn-primary"
          >
            <FiUpload /> Upload Files
          </button>
        </div>
      </div>

      {fileStats && (
        <div className="file-stats">
          <div className="stat-card">
            <div className="stat-value">{fileStats.total_files}</div>
            <div className="stat-label">Total Files</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatFileSize(fileStats.total_size_bytes)}</div>
            <div className="stat-label">Total Size</div>
          </div>
          {Object.entries(fileStats.files_by_category).map(([category, count]) => (
            <div key={category} className="stat-card">
              <div className="stat-value">{count}</div>
              <div className="stat-label">{category.replace('_', ' ')}</div>
            </div>
          ))}
        </div>
      )}

      {showAdminControls && (
        <div className="admin-controls">
          <h3>Admin File Processing Controls</h3>
          
          {processingStatus && (
            <div className="processing-status">
              <div className="status-row">
                <span>Background Processing:</span>
                <span className={processingStatus.configuration.background_processing_enabled ? 'enabled' : 'disabled'}>
                  {processingStatus.configuration.background_processing_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="status-row">
                <span>Pause During Queries:</span>
                <span>{processingStatus.configuration.pause_during_queries ? 'Yes' : 'No'}</span>
              </div>
              <div className="status-row">
                <span>Batch Size:</span>
                <span>{processingStatus.configuration.batch_size} files</span>
              </div>
              <div className="status-row">
                <span>Timeout:</span>
                <span>{processingStatus.configuration.timeout_seconds}s</span>
              </div>
              
              <div className="status-stats">
                <div className="stat-item pending">
                  <span className="stat-count">{processingStatus.status.files_pending_processing}</span>
                  <span className="stat-label">Pending</span>
                </div>
                <div className="stat-item processed">
                  <span className="stat-count">{processingStatus.status.files_processed}</span>
                  <span className="stat-label">Processed</span>
                </div>
                <div className="stat-item failed">
                  <span className="stat-count">{processingStatus.status.files_failed}</span>
                  <span className="stat-label">Failed</span>
                </div>
              </div>
            </div>
          )}
          
          <div className="admin-actions">
            <button
              onClick={triggerManualProcessing}
              disabled={adminProcessing}
              className="btn btn-primary"
              title="Manually process up to 5 pending files"
            >
              <FiPlay /> Process Files Now
            </button>
            
            <button
              onClick={resetFailedFiles}
              disabled={adminProcessing || !processingStatus || processingStatus.status.files_failed === 0}
              className="btn btn-warning"
              title="Reset all failed files to pending status"
            >
              <FiRotateCcw /> Reset Failed Files
            </button>
            
            {selectedFiles.size > 0 && (
              <button
                onClick={resetSelectedFiles}
                disabled={adminProcessing}
                className="btn btn-info"
                title={`Reset ${selectedFiles.size} selected files`}
              >
                <FiRotateCcw /> Reset Selected ({selectedFiles.size})
              </button>
            )}
            
            <button
              onClick={loadProcessingStatus}
              disabled={adminProcessing}
              className="btn btn-secondary"
              title="Refresh processing status"
            >
              <FiRefreshCw /> Refresh Status
            </button>
          </div>
        </div>
      )}

      <div
        className={`file-drop-zone ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
          style={{ display: 'none' }}
          accept=".pdf,.doc,.docx,.txt,.md,.json,.csv,.xlsx,.xls,.pptx,.ppt"
        />
        <FiUpload className="drop-icon" />
        <p>Drag and drop files here or click to browse</p>
        <p className="file-types">Supported: PDF, DOC, DOCX, TXT, MD, JSON, CSV, XLSX, XLS, PPTX, PPT</p>
      </div>

      {/* Upload Progress */}
      {uploadProgress.size > 0 && (
        <div className="upload-progress-container">
          <h3>Upload Progress</h3>
          {Array.from(uploadProgress.values()).map((progress) => (
            <div key={progress.fileName} className="upload-progress-item">
              <div className="progress-header">
                <span className="progress-filename">{progress.fileName}</span>
                <span className={`progress-status ${progress.status}`}>
                  {progress.status === 'uploading' && 'Uploading...'}
                  {progress.status === 'processing' && 'Processing...'}
                  {progress.status === 'completed' && <><FiCheckCircle /> Completed</>}
                  {progress.status === 'failed' && <><FiAlertCircle /> Failed</>}
                </span>
              </div>
              <div className="progress-bar-container">
                <div 
                  className={`progress-bar ${progress.status}`}
                  style={{ width: `${progress.progress}%` }}
                />
              </div>
              {progress.error && (
                <div className="progress-error">{progress.error}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="files-container">
        {loading ? (
          <div className="loading">Loading files...</div>
        ) : (
          <div className="file-grid">
            {files.map((file) => (
              <div key={file.file_id} className={`file-card ${selectedFiles.has(file.file_id) ? 'selected' : ''}`}>
                <div className="file-header">
                  <div className="file-selection">
                    <input
                      type="checkbox"
                      checked={selectedFiles.has(file.file_id)}
                      onChange={() => toggleFileSelection(file.file_id)}
                      className="file-checkbox"
                    />
                  </div>
                  <div className="file-icon-container">
                    <FiFile className="file-icon" />
                    {file.status === 'processed' && <FiCheckCircle className="status-icon success" />}
                    {file.status === 'processing' && <FiRefreshCw className="status-icon processing spinning" />}
                    {file.status === 'failed' && <FiAlertCircle className="status-icon error" />}
                    {!['processed', 'processing', 'failed'].includes(file.status) && <FiClock className="status-icon pending" />}
                  </div>
                  <div className="file-info">
                    <h4 className="file-name" title={file.filename}>
                      {file.filename}
                    </h4>
                    <div className="file-meta">
                      <span>{formatFileSize(file.file_size)}</span>
                      <span>•</span>
                      <span>{file.category}</span>
                    </div>
                  </div>
                </div>
                
                <div className="file-details">
                  <div className="detail-item">
                    <FiClock /> {formatDate(file.uploaded_at)}
                  </div>
                  {file.tags.length > 0 && (
                    <div className="file-tags">
                      {file.tags.map((tag, idx) => (
                        <span key={idx} className="tag">
                          <FiTag /> {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  {file.status === 'processed' && (
                    <div className="processing-info">
                      <span>Chunks: {file.chunk_count}</span>
                      <span>Vectors: {file.vector_count}</span>
                    </div>
                  )}
                </div>
                
                <div className="file-actions">
                  <button
                    onClick={() => downloadFile(file.file_id, file.filename)}
                    className="btn btn-icon"
                    title="Download"
                  >
                    <FiDownload />
                  </button>
                  {file.status !== 'processed' && file.status !== 'processing' && (
                    <button
                      onClick={() => processFile(file.file_id)}
                      className="btn btn-icon"
                      title="Process for RAG"
                    >
                      <FiRefreshCw />
                    </button>
                  )}
                  <button
                    onClick={() => deleteFile(file.file_id)}
                    className="btn btn-icon btn-danger"
                    title="Delete"
                    disabled={file.isDeleting}
                  >
                    {file.isDeleting ? <FiRefreshCw className="spin" /> : <FiTrash2 />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileManagement;